<script lang="ts" module>
  import { z } from 'zod';

  const ButtonThemePropertiesSchema = z
    .object({
      foregroundColor: z.string().optional(),
      backgroundColor: z.string().optional(),
      borderColor: z.string().optional(),
      borderSize: z.string().optional(),
      opacity: z.string().optional()
    })
    .optional();

  const ButtonThemeStatesSchema = z
    .object({
      default: ButtonThemePropertiesSchema,
      hoverFocus: ButtonThemePropertiesSchema,
      active: ButtonThemePropertiesSchema
    })
    .optional();

  export const ButtonTheme = z.object({
    borderRadius: z.string().optional(),
    primaryButton: ButtonThemeStatesSchema,
    secondaryButton: ButtonThemeStatesSchema
  });
</script>

<script lang="ts">
  import { cx } from '@emotion/css';
  import type { HTMLAnchorAttributes } from 'svelte/elements';
  import { onMount, type Snippet } from 'svelte';
  import throttle from 'lodash/throttle';
  import { ResponsiveVars, type FluidProp } from '@twist/core';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  interface Props extends HTMLAnchorAttributes {
    /**
     * Used with TwistGroup component. Will be true/false is part of a Group, otherwise undefined.
     */
    groupActive?: boolean | undefined;
    /**
     * Style variant of button.
     */
    variant?: 'fill' | 'outline' | 'ghost';
    /**
     * Color variant of button.
     */
    inverse?: boolean;
    /**
     * Style properties
     */
    fontSize?: FluidProp;
    py?: FluidProp;
    px?: FluidProp;
    height?: FluidProp;
    fullWidth?: boolean;
    /**
     * Absolute or relative URL that the hyperlink points to.
     * If href is set, `<a>` tag will be rendered.
     */
    href?: string | undefined;
    /**
     * Where the href URL loads. See
     * [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#attr-target)
     * for usage details. Only applies if `href` is set.
     */
    target?: '_blank' | '_parent' | '_self' | '_top' | undefined;
    /**
     * Indicates relationship of documents. See
     * [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Link_types)
     * for usage details. Only applies if `href` is set.
     */
    rel?:
      | 'alternate'
      | 'author'
      | 'bookmark'
      | 'external'
      | 'help'
      | 'license'
      | 'next'
      | 'nofollow'
      | 'noreferrer'
      | 'noopener'
      | 'prev'
      | 'search'
      | 'tag'
      | undefined;
    /**
     * If arrow should rotate to follow pointer position
     * or be disabled. Only applies to fill variants.
     */
    motion?: 'when-active' | 'when-inactive' | 'never';
    iconElement?: HTMLSpanElement;
    children?: Snippet;
    icon?: Snippet;
    [key: string]: unknown;
  }

  let {
    groupActive = undefined,
    variant = 'fill',
    inverse = false,
    fontSize = toFluidVarInput(12, 12),
    py = toFluidVarInput(9, 9),
    px = toFluidVarInput(15, 15),
    height = toFluidVarInput(36, 36),
    fullWidth = false,
    href = undefined,
    target = undefined,
    rel = undefined,
    motion = 'when-inactive',
    iconElement = $bindable(undefined),
    children,
    icon,
    ...rest
  }: Props = $props();

  // non-tabbable if it is part of a hover group
  const tabindex = typeof groupActive === 'boolean' ? -1 : 0;

  type Position = { X: number; Y: number };

  let transformRotation = $state(0);

  function iconPosition() {
    if (!iconElement) {
      return;
    }

    const { left, top } = iconElement.getBoundingClientRect();

    // getBoundingClientRect is relative to viewport.
    // In order to get values relative to entire document,
    // we also need to account for scrollX and scrollY
    return {
      X: left + window.scrollX + iconElement.offsetWidth / 2,
      Y: top + window.scrollY + iconElement.offsetHeight / 2
    };
  }

  export function calculateIconAngle(mouseXY: Position) {
    const iconXY = iconPosition();

    if (!iconXY) {
      return;
    }

    const diffX = iconXY.X - mouseXY.X;
    const diffY = iconXY.Y - mouseXY.Y;
    const tan = diffY / diffX;

    let atan = (Math.atan(tan) * 180) / Math.PI;
    if (diffY > 0 && diffX > 0) {
      atan += 180;
    } else if (diffY < 0 && diffX > 0) {
      atan -= 180;
    }

    transformRotation = atan;
  }

  const throttledCalculateIconAngle = throttle(calculateIconAngle, 16);

  onMount(() => {
    if (!iconElement || variant !== 'fill') {
      return;
    }

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('wheel', handleWheel);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('wheel', handleWheel);
    };
  });

  function handleMouseMove(event: MouseEvent) {
    if (motion === 'never') {
      return;
    }

    throttledCalculateIconAngle({ X: event.pageX, Y: event.pageY });
  }

  function handleWheel(event: WheelEvent) {
    if (motion === 'never') {
      return;
    }

    throttledCalculateIconAngle({ X: event.pageX, Y: event.pageY });
  }

  function handleClick() {
    // remove focus after click
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }

  const responsiveVars = new ResponsiveVars()
    .addFluid('height', height)
    .addFluid('font-size', fontSize)
    .addFluid('text-padding-y', py)
    .addFluid('text-padding-x', px)
    .addFluid('primary-icon-margin-left', [
      ['min', 6],
      ['sm', 'lg', 6, 12],
      ['lg', 'max', 6, 9],
      ['max', 9]
    ])
    .toCSS();

  const elementType = () => {
    // use span if part of a group to prevent node_invalid_placement_ssr
    // error: node_invalid_placement_ssr `<a>` cannot be a child of `<a>` can lead to hydration_mismatch errors
    if (typeof groupActive === 'boolean') {
      return 'span';
    }
    return href ? 'a' : 'button';
  };
</script>

<svelte:element
  this={elementType()}
  role={href ? 'link' : 'button'}
  class:groupActive
  class:full-width={fullWidth}
  class:inverse
  class:animate-when-active={motion === 'when-active'}
  class:animate-when-inactive={motion === 'when-inactive'}
  class={cx(responsiveVars, 'button', `variant-${variant}`)}
  style={`--transform-rotation: ${motion === 'never' || variant === 'outline' ? 0 : transformRotation};`}
  {href}
  {target}
  {rel}
  {tabindex}
  onclick={handleClick}
  {...rest}
>
  {#if children}
    <span class="text">
      {@render children?.()}
    </span>
  {/if}

  <span class="icon-outer" bind:this={iconElement}>
    {#if icon}
      <span class="icon">
        {@render icon?.()}
      </span>
    {/if}
  </span>
</svelte:element>

<style lang="scss">
  @import 'src/scss/media';
  @import 'src/scss/button';
  @import 'src/scss/variables';

  @mixin iconRotate {
    // only rotate for mouse/trackpad devices
    @media (hover: hover) and (pointer: fine) {
      transform: rotate(calc(var(--transform-rotation) * 1deg));
      transition: none;
    }

    @media (prefers-reduced-motion) {
      transform: none;
    }
  }

  @mixin iconNoRotation {
    transform: rotate(0deg);
    transition: transform 0.2s linear;
  }

  @mixin buttonActive {
    &.groupActive,
    &:hover,
    &:focus {
      @content;
    }
  }

  .button {
    display: inline-flex;
    width: max-content;
    height: var(--height);
    padding: 0;
    border: none;
    background: none;
    text-decoration: none;
    text-transform: uppercase;
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
    }
    .text {
      height: 100%;
      display: inline-flex;
      justify-content: center;
      align-items: center;
      font-size: var(--font-size);
      font-family: var(--font-family-primary);
      font-weight: 500;
      line-height: 100%;
      letter-spacing: 0.96px;
    }

    .icon-outer {
      height: 100%;
    }

    .icon {
      display: inline-block;
      height: 100%;
      line-height: 120%;
    }

    &.variant-fill {
      opacity: var(--theme-button-primary-button-default-opacity, 1);

      .text {
        color: var(--theme-button-primary-button-default-foreground-color, $text-default-color);
        background-color: var(
          --theme-button-primary-button-default-background-color,
          $background-depth-2-color
        );
        border-radius: var(--theme-button-border-radius, 100px);
        padding: var(--text-padding-y) var(--text-padding-x);
      }

      .icon {
        margin-left: var(--primary-icon-margin-left);

        :global(svg .foreground) {
          fill: var(--theme-button-primary-button-default-foreground-color, $text-default-color);
        }

        :global(svg .background) {
          fill: var(
            --theme-button-primary-button-default-background-color,
            $background-depth-2-color
          );
        }
      }

      &.groupActive:active:not(:disabled),
      &:active:not(:disabled) {
        opacity: var(--theme-button-primary-button-active-opacity, 0.6);

        .text {
          color: var(--theme-button-primary-button-active-foreground-color, $text-default-color);
          background-color: var(
            --theme-button-primary-button-active-background-color,
            $background-depth-2-hover-color
          );
        }

        .icon {
          :global(svg .foreground) {
            fill: var(--theme-button-primary-button-active-foreground-color, $text-default-color);
          }

          :global(svg .background) {
            fill: var(
              --theme-button-primary-button-active-background-color,
              $background-depth-2-hover-color
            );
          }
        }
      }

      @include buttonHoverFocus {
        outline: none;
        transition: 0.1s ease-in-out;
        opacity: var(--theme-button-primary-button-hover-focus-opacity, 1);

        .text {
          color: var(
            --theme-button-primary-button-hover-focus-foreground-color,
            $text-default-color
          );
          background-color: var(
            --theme-button-primary-button-hover-focus-background-color,
            $background-depth-2-hover-color
          );
        }

        .icon {
          :global(svg .foreground) {
            fill: var(
              --theme-button-primary-button-hover-focus-foreground-color,
              $text-default-color
            );
          }

          :global(svg .background) {
            fill: var(
              --theme-button-primary-button-hover-focus-background-color,
              $background-depth-2-hover-color
            );
          }
        }
      }

      &.animate-when-active {
        .icon {
          @include iconNoRotation();
        }

        @include buttonActive {
          .icon {
            @include iconRotate();
          }
        }
      }

      &.animate-when-inactive {
        .icon {
          @include iconRotate();
        }

        @include buttonActive {
          .icon {
            @include iconNoRotation();
          }
        }
      }

      // TODO disabled button color currently cannot be set via theme
      &:disabled {
        .text {
          color: $text-interactive-default-disabled-color;
          background-color: $background-depth-2-disabled-color;
        }

        .icon {
          :global(svg .foreground) {
            fill: $text-interactive-default-disabled-color;
          }

          :global(svg .background) {
            fill: $background-depth-2-disabled-color;
          }
        }
      }
    }

    &.variant-fill.inverse {
      opacity: var(--theme-button-primary-button-default-opacity, 1);

      .text {
        color: var(--theme-button-primary-button-default-foreground-color, $text-inverse-color);
        background-color: var(
          --theme-button-primary-button-default-background-color,
          $background-inverse-color
        );
        border-radius: var(--theme-button-border-radius, 100px);
        padding: var(--text-padding-y) var(--text-padding-x);
      }

      .icon {
        margin-left: var(--primary-icon-margin-left);

        :global(svg .foreground) {
          fill: var(--theme-button-primary-button-default-foreground-color, $text-inverse-color);
        }

        :global(svg .background) {
          fill: var(
            --theme-button-primary-button-default-background-color,
            $background-inverse-color
          );
        }
      }

      &.groupActive:active:not(:disabled),
      &:active:not(:disabled) {
        opacity: var(--theme-button-primary-button-active-opacity, 0.6);

        .text {
          color: var(
            --theme-button-primary-button-active-foreground-color,
            $text-interactive-inverse-hover-color
          );
          background-color: var(
            --theme-button-primary-button-active-background-color,
            $background-inverse-hover-color
          );
        }

        .icon {
          :global(svg .foreground) {
            fill: var(
              --theme-button-primary-button-active-foreground-color,
              $text-interactive-inverse-hover-color
            );
          }

          :global(svg .background) {
            fill: var(
              --theme-button-primary-button-active-background-color,
              $background-inverse-hover-color
            );
          }
        }
      }

      @include buttonHoverFocus {
        outline: none;
        transition: 0.1s ease-in-out;
        opacity: var(--theme-button-primary-button-hover-focus-opacity, 1);

        .text {
          color: var(
            --theme-button-primary-button-hover-focus-foreground-color,
            $text-interactive-inverse-hover-color
          );
          background-color: var(
            --theme-button-primary-button-hover-focus-background-color,
            $background-inverse-hover-color
          );
        }

        .icon {
          :global(svg .foreground) {
            fill: var(
              --theme-button-primary-button-hover-focus-foreground-color,
              $text-interactive-inverse-hover-color
            );
          }

          :global(svg .background) {
            fill: var(
              --theme-button-primary-button-hover-focus-background-color,
              $background-inverse-hover-color
            );
          }
        }
      }

      &.animate-when-active {
        .icon {
          @include iconNoRotation();
        }

        @include buttonActive {
          .icon {
            @include iconRotate();
          }
        }
      }

      &.animate-when-inactive {
        .icon {
          @include iconRotate();
        }

        @include buttonActive {
          .icon {
            @include iconNoRotation();
          }
        }
      }

      // TODO disabled button color currently cannot be set via theme
      &:disabled {
        .text {
          color: $text-interactive-default-disabled-color;
          background-color: $background-inverse-disabled-color;
        }

        .icon {
          :global(svg .foreground) {
            fill: $text-interactive-inverse-disabled-color;
          }

          :global(svg .background) {
            fill: $background-inverse-disabled-color;
          }
        }
      }
    }

    &.variant-outline {
      align-items: center;
      box-sizing: border-box;
      border: var(--theme-button-secondary-button-default-border-size, $stroke-width-1) solid
        var(--theme-button-secondary-button-default-border-color, $border-interactive-default-color);
      border-radius: var(--theme-button-border-radius, 100px);
      padding: var(--text-padding-y) var(--text-padding-x);
      background-color: transparent;
      opacity: var(--theme-button-secondary-button-default-opacity, 1);

      .text {
        color: var(
          --theme-button-secondary-button-default-foreground-color,
          $text-interactive-default-color
        );
      }

      .icon {
        display: flex;
        align-items: center;
        color: var(
          --theme-button-secondary-button-default-foreground-color,
          $text-interactive-default-color
        );

        :global(svg) {
          fill: var(
            --theme-button-secondary-button-default-foreground-color,
            $text-interactive-default-color
          );
        }
      }

      &.groupActive:active,
      &:active {
        border: var(--theme-button-secondary-button-active-border-size, $stroke-width-1_5) solid
          var(
            --theme-button-secondary-button-active-border-color,
            $border-interactive-default-hover-color
          );
        background-color: transparent;
        opacity: var(--theme-button-secondary-button-active-opacity, 0.6);

        .text {
          color: var(
            --theme-button-secondary-button-active-foreground-color,
            $text-interactive-default-hover-color
          );
        }

        .icon {
          color: var(
            --theme-button-secondary-button-active-foreground-color,
            $text-interactive-default-hover-color
          );

          :global(svg) {
            fill: var(
              --theme-button-secondary-button-active-foreground-color,
              $text-interactive-default-hover-color
            );
          }
        }
      }

      @include buttonHoverFocus {
        border: var(--theme-button-secondary-button-hover-focus-border-size, $stroke-width-1_5)
          solid
          var(
            --theme-button-secondary-button-hover-focus-border-color,
            $border-interactive-default-hover-color
          );
        background-color: transparent;
        transition: background-color 0.1s ease-in-out;
        outline: none;
        opacity: var(--theme-button-secondary-button-hover-focus-opacity, 1);

        .text {
          color: var(
            --theme-button-secondary-button-hover-focus-foreground-color,
            $text-interactive-default-hover-color
          );
        }

        .icon {
          color: var(
            --theme-button-secondary-button-hover-focus-foreground-color,
            $text-interactive-default-hover-color
          );

          :global(svg) {
            fill: var(
              --theme-button-secondary-button-hover-focus-foreground-color,
              $text-interactive-default-hover-color
            );
          }
        }
      }

      // TODO disabled button color currently cannot be set via theme
      &:disabled {
        border: var(--theme-button-secondary-button-default-border-size, $stroke-width-1) solid
          $border-interactive-default-disabled-color;

        .text,
        .icon {
          color: $text-interactive-default-disabled-color;

          :global(svg) {
            fill: $text-interactive-default-disabled-color;
          }
        }
      }
    }

    &.variant-outline.inverse {
      border: var(--theme-button-secondary-button-default-border-size, $stroke-width-1) solid
        var(--theme-button-secondary-button-default-border-color, $border-interactive-inverse-color);

      .text {
        color: var(
          --theme-button-secondary-button-default-foreground-color,
          $text-interactive-inverse-color
        );
      }

      .icon {
        color: var(
          --theme-button-secondary-button-default-foreground-color,
          $text-interactive-inverse-color
        );

        :global(svg) {
          fill: var(
            --theme-button-secondary-button-default-foreground-color,
            $text-interactive-inverse-color
          );
        }
      }

      &.groupActive:active,
      &:active {
        border: var(--theme-button-secondary-button-active-border-size, $stroke-width-1_5) solid
          var(
            --theme-button-secondary-button-active-border-color,
            $border-interactive-inverse-hover-color
          );

        .text {
          color: var(
            --theme-button-secondary-button-active-foreground-color,
            $text-interactive-inverse-color
          );
        }

        .icon {
          color: var(
            --theme-button-secondary-button-active-foreground-color,
            $text-interactive-inverse-color
          );

          :global(svg) {
            fill: var(
              --theme-button-secondary-button-active-foreground-color,
              $text-interactive-inverse-color
            );
          }
        }
      }

      @include buttonHoverFocus {
        border: var(--theme-button-secondary-button-hover-focus-border-size, $stroke-width-1_5)
          solid
          var(
            --theme-button-secondary-button-hover-focus-border-color,
            $border-interactive-inverse-hover-color
          );
        .text {
          color: var(
            --theme-button-secondary-button-hover-focus-foreground-color,
            $text-interactive-inverse-color
          );
        }

        .icon {
          color: var(
            --theme-button-secondary-button-hover-focus-foreground-color,
            $text-interactive-inverse-color
          );

          :global(svg) {
            fill: var(
              --theme-button-secondary-button-hover-focus-foreground-color,
              $text-interactive-inverse-color
            );
          }
        }
      }

      // TODO disabled button color currently cannot be set via theme
      &:disabled {
        border: var(--theme-button-secondary-button-default-border-size, $stroke-width-1) solid
          $border-interactive-inverse-disabled-color;

        .text,
        .icon {
          color: $text-interactive-inverse-disabled-color;

          :global(svg) {
            fill: $text-interactive-inverse-disabled-color;
          }
        }
      }
    }

    // TODO ghost button currently cannot be set via theme
    &.variant-ghost {
      align-items: center;
      box-sizing: border-box;
      border: none;
      border-radius: 100px;
      padding: var(--text-padding-y) var(--text-padding-x);
      background-color: transparent;
      opacity: 1;

      .text {
        color: $text-default-color;
      }

      .icon {
        display: flex;
        align-items: center;
        color: $text-default-color;

        :global(svg) {
          fill: $text-default-color;
        }
      }

      &.groupActive:active,
      &:active {
        background-color: $background-depth-1-color;
        opacity: 0.6;

        .text {
          color: $text-default-color;
        }

        .icon {
          color: $text-default-color;

          :global(svg) {
            fill: $text-default-color;
          }
        }
      }

      @include buttonHoverFocus {
        background-color: $background-depth-1-color;
        transition: background-color 0.1s ease-in-out;
        outline: none;
        opacity: 1;

        .text {
          color: $text-default-color;
        }

        .icon {
          color: $text-default-color;

          :global(svg) {
            fill: $text-default-color;
          }
        }
      }

      // TODO disabled button color currently cannot be set via theme
      &:disabled {
        background-color: transparent;

        .text,
        .icon {
          color: $text-default-disabled-color;

          :global(svg) {
            fill: $text-default-disabled-color;
          }
        }
      }
    }

    // TODO ghost button currently cannot be set via theme
    &.variant-ghost.inverse {
      .text {
        color: $text-interactive-inverse-color;
      }

      .icon {
        color: $text-interactive-inverse-color;

        :global(svg) {
          fill: $text-interactive-inverse-color;
        }
      }

      &.groupActive:active,
      &:active {
        background-color: $background-inverse-hover-color;

        .text {
          color: $text-interactive-inverse-hover-color;
        }

        .icon {
          color: $text-interactive-inverse-hover-color;

          :global(svg) {
            fill: $text-interactive-inverse-hover-color;
          }
        }
      }

      @include buttonHoverFocus {
        background-color: $background-inverse-hover-color;

        .text {
          color: $text-interactive-inverse-hover-color;
        }

        .icon {
          color: $text-interactive-inverse-hover-color;

          :global(svg) {
            fill: $text-interactive-inverse-hover-color;
          }
        }
      }

      // TODO disabled button color currently cannot be set via theme
      &:disabled {
        background-color: transparent;

        .text,
        .icon {
          color: $text-interactive-inverse-disabled-color;

          :global(svg) {
            fill: $text-interactive-inverse-disabled-color;
          }
        }
      }
    }
  }

  .full-width {
    width: 100%;

    .text {
      width: 100%;
    }
  }
</style>
